import { Component, OnDestroy, OnInit } from '@angular/core';
import { FormBuilder, FormGroup } from '@angular/forms';
import { DynamicDialogConfig, DynamicDialogRef } from 'primeng/dynamicdialog';
import { Subject } from 'rxjs';
import { first, takeUntil } from 'rxjs/operators';

import { User } from '../../models/user';
import { AccountService } from '../../services/account.service';
import { UserService } from '../../services/user.service';
import { ModalService } from '../../services/modal.service';
import { PLACEMENT_OPTIONS } from '../content-profile-list/content-profile-list.component';

/** Matches the Settings page — servedAreas is capped there too. */
const MAX_SERVED_AREAS = 5;

type ServedArea = { label: string; state?: string; regionSlug?: string };

/**
 * Website → Profiles → edit (#184).
 *
 * The public-facing half of a user record: the copy and photo that render on
 * picaluna.com. Deliberately NOT the account fields (roles, regions, access) —
 * those stay on the user admin page, because a Content Manager fixing SEO copy
 * has no business changing who can log in.
 *
 * Saves through PATCH /user/:id/profile — the admin path. NOT the route the
 * Settings page uses: that one binds the target to the signed-in caller (C5),
 * so editing someone else wrote their content onto your own row. The upsert
 * leaves any column the payload omits alone, which is why this can send only
 * the public-facing fields and cannot disturb roles, regions or access.
 */
@Component({
  selector: 'app-content-profile',
  templateUrl: './content-profile.component.html',
})
export class ContentProfileComponent implements OnInit, OnDestroy {
  form!: FormGroup;
  user!: User;
  saving = false;
  placementOptions = PLACEMENT_OPTIONS;

  maxServedAreas = MAX_SERVED_AREAS;
  regionPick: any = null;
  regionOptions: { label: string; value: ServedArea }[] = [];

  private unsubscribe$ = new Subject<void>();

  constructor(
    private formBuilder: FormBuilder,
    private accountService: AccountService,
    private userService: UserService,
    private modalService: ModalService,
    public ref: DynamicDialogRef,
    public config: DynamicDialogConfig
  ) {}

  ngOnInit(): void {
    this.user = this.config.data?.user;
    this.form = this.formBuilder.group({
      jobTitle: [this.user?.jobTitle ?? ''],
      bio: [this.user?.bio ?? ''],
      isPublicProfile: [!!this.user?.isPublicProfile],
      placements: [[...(this.user?.placements ?? [])]],
      servedAreas: [[...((this.user as any)?.servedAreas ?? [])] as ServedArea[]],
      website: [(this.user as any)?.website ?? ''],
      linkedin: [(this.user as any)?.linkedin ?? ''],
      facebook: [(this.user as any)?.facebook ?? ''],
      instagram: [(this.user as any)?.instagram ?? ''],
      tiktok: [(this.user as any)?.tiktok ?? ''],
      youtube: [(this.user as any)?.youtube ?? ''],
    });

    /* Canonical region list (Website → Regions) — the same records that drive
       picaluna.com's location pages, so a pick here joins exactly. Mirrors the
       Settings page loader, including its path quirk: the service runs
       objectsToArrays on res.data.data, so collections arrive as ARRAYS under
       data.data rather than id-keyed maps under data. */
    this.accountService
      .get(true)
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe({
        next: (res: any) => {
          const content = res?.data?.data?.content ?? res?.data?.content ?? {};
          const raw = content.regions ?? [];
          const regions = (Array.isArray(raw) ? raw : Object.values(raw)) as any[];
          this.regionOptions = regions
            .filter((r) => r && r._meta?.deleted !== true && r.published !== false && r.slug)
            .sort((a, b) => (a.order ?? 99) - (b.order ?? 99))
            .map((r) => ({
              label: r.state ? `${r.name} (${r.state})` : r.name,
              value: { regionSlug: r.slug, label: r.name, state: r.state },
            }));
        },
        error: () => (this.regionOptions = []),
      });
  }

  ngOnDestroy(): void {
    this.unsubscribe$.next();
    this.unsubscribe$.complete();
  }

  get servedAreas(): ServedArea[] {
    return (this.form?.get('servedAreas')?.value as ServedArea[]) ?? [];
  }

  onAddRegion(pick: ServedArea | null): void {
    // Reset regardless — it is an "add" affordance, not a selection.
    setTimeout(() => (this.regionPick = null));
    if (!pick) return;
    const current = this.servedAreas;
    if (current.length >= MAX_SERVED_AREAS) return;
    if (current.some((a) => a.regionSlug === pick.regionSlug)) return;
    this.form.get('servedAreas')?.setValue([...current, pick]);
  }

  removeServedArea(index: number): void {
    const current = [...this.servedAreas];
    current.splice(index, 1);
    this.form.get('servedAreas')?.setValue(current);
  }

  /** Published with no placement is legal but invisible — warn, never block. */
  get strandedWarning(): boolean {
    return (
      !!this.form?.get('isPublicProfile')?.value &&
      (this.form?.get('placements')?.value || []).length === 0
    );
  }

  /**
   * The mirror case, and the far commoner one: pages are chosen but the master
   * switch is off. The backfill set a placement on every profile carrying a
   * bio without touching isPublicProfile, so most records land here — and the
   * picker alone reads as a promise the profile is live.
   */
  get unpublishedWarning(): boolean {
    return (
      !this.form?.get('isPublicProfile')?.value &&
      (this.form?.get('placements')?.value || []).length > 0
    );
  }

  onSave(): void {
    if (this.saving) return;
    this.saving = true;
    const v = this.form.value;
    this.userService
      .updateProfileAsAdmin(this.user.id, {
        // '' not null, same reason as the links below: the upsert skips a
        // NULL column, so an emptied bio would come back on the next reload.
        jobTitle: (v.jobTitle || '').trim(),
        bio: (v.bio || '').trim(),
        isPublicProfile: v.isPublicProfile ? 1 : 0,
        // Always sent, even when empty: [] is a meaningful value here (shown
        // nowhere), and the upsert only skips a column that is NULL.
        placements: v.placements || [],
        servedAreas: v.servedAreas || [],
        // '' not null: the upsert skips a NULL, so an emptied field would keep
        // its old value and a removed link could never be removed.
        website: (v.website || '').trim(),
        linkedin: (v.linkedin || '').trim(),
        facebook: (v.facebook || '').trim(),
        instagram: (v.instagram || '').trim(),
        tiktok: (v.tiktok || '').trim(),
        youtube: (v.youtube || '').trim(),
      })
      .pipe(first())
      .subscribe({
        next: () => {
          this.saving = false;
          this.ref.close({
            ...this.user,
            jobTitle: (v.jobTitle || '').trim(),
            bio: (v.bio || '').trim(),
            isPublicProfile: v.isPublicProfile ? 1 : 0,
            placements: v.placements || [],
            servedAreas: v.servedAreas || [],
            website: (v.website || '').trim(),
            linkedin: (v.linkedin || '').trim(),
            facebook: (v.facebook || '').trim(),
            instagram: (v.instagram || '').trim(),
            tiktok: (v.tiktok || '').trim(),
            youtube: (v.youtube || '').trim(),
          } as any);
        },
        error: (err) => {
          this.saving = false;
          this.modalService.error(err?.error?.error || err);
        },
      });
  }

  onCancel(): void {
    this.ref.close(undefined);
  }
}
